/* eslint-disable react/jsx-props-no-spreading */
import React, { ReactNode } from 'react';
import MuiTabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Add from '@mui/icons-material/Add';
import Stack from '@mui/material/Stack';
import kebabCase from 'lodash/kebabCase';
import Button from './button';
import Tooltip from './info-elements/tooltip';
import ConditionalWrapper from './conditional-wrapper';

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}
export interface TabItem {
  label: string;
  tabPanelComponent: ReactNode;
  actionButtonLabel: string;
  isEditable?: boolean;
  disabledActionButtonTooltip?: string;
}

export interface Props {
  tabIndex?: number;
  tabs: TabItem[];
  onTabChange?: (index: number, tab: TabItem) => void;
  onActionButton?: (e: EventTarget & HTMLButtonElement) => void;
}

export default function Tabs({ tabIndex = 0, ...props }: Props) {
  const [value, setValue] = React.useState(tabIndex);

  React.useEffect(() => {
    setValue(tabIndex);
  }, [tabIndex]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    if (props.onTabChange) {
      props.onTabChange(newValue, props.tabs[newValue]);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    props.onActionButton!(e.currentTarget);
  };

  const currentTab = props.tabs[value];

  return (
    <Stack sx={{ width: '100%', height: '100%' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <MuiTabs value={value} onChange={handleChange} sx={{ height: '100%' }}>
          {props.tabs.map(({ label }, index) => (
            <Tab
              key={`tab-${kebabCase(label)}`}
              label={label}
              {...a11yProps(index)}
            />
          ))}
        </MuiTabs>
        {!!props.onActionButton && (
          <ConditionalWrapper
            condition={!(currentTab.isEditable ?? true) && !!currentTab.disabledActionButtonTooltip}
            renderWrapper={() => ((children) => (
              <Tooltip
                arrow
                title={currentTab.disabledActionButtonTooltip ?? ''}
                placement="top"
                overflow={false}
              >
                <span>
                  {children}
                </span>
              </Tooltip>
            ))}
          >
            <Button
              variant="contained"
              size="small"
              onClick={handleClick}
              disabled={!(currentTab.isEditable ?? true)}
              startIcon={<Add />}
            >
              {currentTab.actionButtonLabel}
            </Button>
          </ConditionalWrapper>
        )}
      </Stack>
      <Stack sx={{ height: '100%' }}>
        {currentTab.tabPanelComponent}
      </Stack>
    </Stack>
  );
}
